import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CreatePharmaCompany, EditPharmaCompany } from 'features/pharmaceutical-company';

import { countryModel } from 'entities/country';
import {
  CannotDeleteDialog,
  PharmaCompaniesFilters,
  PharmaCompaniesTable,
  pharmaceuticalCompanyModel,
} from 'entities/pharmaceutical-company';

import {
  CreatePharmaceuticalCompanyDto,
  PharmaceuticalCompany,
  PharmaceuticalCompanyListItem,
  UpdatePharmaceuticalCompanyDto,
} from 'shared/api';
import { useDebouncedSearch } from 'shared/lib';
import { ConfirmDialog, LoadingProgress } from 'shared/ui';

export const PharmaCompanies = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'abbreviatedName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'asc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '20', 10));
  const [byCountry, setByCountry] = useState(searchParams.get('byCountry') ?? '');

  const [editCompany, setEditCompany] = useState<PharmaceuticalCompany | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteCompany, setDeleteCompany] = useState<PharmaceuticalCompanyListItem | null>(null);
  const [blockedCompany, setBlockedCompany] = useState<PharmaceuticalCompanyListItem | null>(null);

  const { data: countriesResp } = countryModel.useAllCountries();
  const countries = countriesResp?.data ?? [];

  const {
    mutate: createMutation,
    isLoading: isCreating,
    isSuccess: isCreateSuccess,
  } = pharmaceuticalCompanyModel.createPharmaCompanyAsync();
  const {
    mutate: updateMutation,
    isLoading: isUpdating,
    isSuccess: isUpdateSuccess,
  } = pharmaceuticalCompanyModel.updatePharmaCompanyAsync();
  const { mutate: deleteMutation, isLoading: isDeleting } = pharmaceuticalCompanyModel.deletePharmaCompanyAsync();

  const debouncedFilter = useDebouncedSearch(filter, 300);

  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
      byCountry: byCountry || undefined,
    }),
    [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, byCountry],
  );

  const { isLoading, isFetching } = pharmaceuticalCompanyModel.getPharmaCompaniesAllAsync(params)(dispatch);
  const { list, pagination } = pharmaceuticalCompanyModel.usePharmaCompanyList();

  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
    };
    if (debouncedFilter) newParams.filter = debouncedFilter;
    if (byCountry) newParams.byCountry = byCountry;
    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, byCountry, setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setFilter(value);
    setPage(1);
  }, []);

  const handleCountryChange = useCallback((code: string) => {
    setByCountry(code);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const handleCreate = useCallback(
    (data: CreatePharmaceuticalCompanyDto) => {
      createMutation(data);
    },
    [createMutation],
  );

  const handleEditOpen = useCallback((company: PharmaceuticalCompanyListItem) => {
    setEditCompany({
      _id: company.id,
      fullName: company.fullName,
      abbreviatedName: company.abbreviatedName,
      russianName: company.russianName,
      country: company.country,
      www: company.www,
      notes: company.notes,
    });
    setIsEditOpen(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setIsEditOpen(false);
    setEditCompany(null);
  }, []);

  const handleUpdate = useCallback(
    (data: UpdatePharmaceuticalCompanyDto) => {
      if (!editCompany) return;
      updateMutation({ id: editCompany._id, data });
    },
    [editCompany, updateMutation],
  );

  const handleDeleteOpen = useCallback((company: PharmaceuticalCompanyListItem) => {
    if (company.vaccineCount > 0) {
      setBlockedCompany(company);
      return;
    }
    setDeleteCompany(company);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteCompany(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteCompany) return;
    deleteMutation(deleteCompany.id, {
      onSuccess: () => setDeleteCompany(null),
    });
  }, [deleteCompany, deleteMutation]);

  if (isLoading && list.length === 0) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column" flex={1} px={3} pt={2}>
      <PharmaCompaniesFilters
        filter={filter}
        byCountry={byCountry}
        countries={countries}
        totalCount={pagination?.total ?? 0}
        onFilterChange={handleFilterChange}
        onCountryChange={handleCountryChange}
        addButton={
          <CreatePharmaCompany
            countries={countries}
            isSuccess={isCreateSuccess}
            isLoading={isCreating}
            onSubmit={handleCreate}
          />
        }
      />

      <PharmaCompaniesTable
        list={list}
        pagination={pagination}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isFetching}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
        onEdit={handleEditOpen}
        onDelete={handleDeleteOpen}
      />

      <EditPharmaCompany
        company={editCompany}
        countries={countries}
        isOpen={isEditOpen}
        isSuccess={isUpdateSuccess}
        isLoading={isUpdating}
        onClose={handleEditClose}
        onSubmit={handleUpdate}
      />

      <ConfirmDialog
        open={!!deleteCompany}
        title="Delete pharmaceutical company"
        message={`Are you sure you want to delete "${
          deleteCompany?.abbreviatedName ?? ''
        }"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isDeleting}
      />

      <CannotDeleteDialog open={!!blockedCompany} company={blockedCompany} onClose={() => setBlockedCompany(null)} />
    </Box>
  );
};
